

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    CircularProgress
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewOrderItineraryModal from 'components/NewOrderItineraryModal';

const OrderItineraries = () => {
    const [itineraries, setItineraries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('order_itineraries');

    useEffect(() => {
        fetchItineraries();
    }, []);

    const fetchItineraries = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/order-itineraries/');
            setItineraries(response.data);
        } catch (error) {
            console.error('Error fetching order itineraries:', error);
            setSnackbar({ open: true, message: 'Error loading order itineraries', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveItinerary = async (payload) => {
        try {
            await axiosInstance.post('/order-itineraries/', payload);
            setSnackbar({ open: true, message: 'Order Itinerary created successfully', severity: 'success' });
            fetchItineraries();
        } catch (error) {
            console.error('Error saving order itinerary:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/order-itinerary/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Itinerary Name",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Travel Order",
            accessor: "travel_order_id",
            width: "15%",
            Cell: ({ row }) => row.original.travel_order?.name || row.original.travel_order_id
        },
        {
            Header: "Trip Logistics",
            accessor: "trip_component_id",
            width: "15%",
            Cell: ({ row }) => row.original.trip_component?.name || row.original.trip_component_id
        },
        {
            Header: "Order Item",
            accessor: "order_item_id",
            width: "15%",
            Cell: ({ row }) => row.original.order_item?.name || row.original.order_item_id
        },
        {
            Header: "Order Pax",
            accessor: "order_pax_id",
            width: "15%",
            Cell: ({ row }) => row.original.order_pax?.passenger_name || row.original.order_pax_id
        },
        {
            Header: "Day Line Item",
            accessor: "day_line_item_id",
            width: "12%",
            Cell: ({ row }) => row.original.day_line_item?.name || row.original.day_line_item_id || '-'
        },
        {
            Header: "Travefy Sync",
            accessor: "travefy_sync",
            width: "10%",
            Cell: ({ value }) => value ? 'Yes' : 'No'
        },
        {
            Header: "Created At",
            accessor: "created_at",
            width: "3%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: itineraries,
    }), [itineraries]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Order Itineraries</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Itinerary
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : itineraries.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Order Itinerary" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewOrderItineraryModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveItinerary}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default OrderItineraries;

